// =============================================================================
// vouchers — supabase RPC wrappers for discount-code management
// =============================================================================
import { supabase } from './supabase';

export type DiscountKind = 'percent' | 'flat';

export interface DiscountCodeRow {
  id: string;
  code: string;
  kind: DiscountKind;
  percent_off: number | null;
  flat_off_cents: number | null;
  max_uses: number | null;
  uses_count: number;
  exhausted: boolean;
  expired: boolean;
  not_yet_valid: boolean;
  valid_from: string | null;
  valid_until: string | null;
  is_active: boolean;
  notes: string | null;
  created_at: string;
  created_by: string | null;
  experience_id: string | null;
  experience_name: string | null;
  // Cap on tickets a percent code covers (gift vouchers = ticket_count).
  max_party_size?: number | null;
  // Buyer of the linked gift-voucher purchase (latest voucher_purchases row).
  // Null for hand-made, bulk and TEGOED codes, and on payloads from an older
  // fn_admin_list_discount_codes.
  buyer_email?: string | null;
  buyer_name?: string | null;
  purchase_status?: VoucherPurchaseStatus | null;
}

export type VoucherPurchaseStatus = 'pending' | 'paid' | 'refunded' | 'failed';

// Status pill for a code (list, search results, detail header).
export function voucherStatusOf(
  row: Pick<DiscountCodeRow, 'is_active' | 'exhausted' | 'expired' | 'not_yet_valid'>,
): { label: string; className: string } {
  if (!row.is_active)    return { label: 'Inactief', className: 'voucher-status-off' };
  if (row.exhausted)     return { label: 'Op',       className: 'voucher-status-off' };
  if (row.expired)       return { label: 'Verlopen', className: 'voucher-status-off' };
  if (row.not_yet_valid) return { label: 'Komt',     className: 'voucher-status-warn' };
  return { label: 'Actief', className: 'voucher-status-ok' };
}

// -----------------------------------------------------------------------------
// Voucher detail (/voucher/:code) — booking.fn_admin_voucher_detail
// -----------------------------------------------------------------------------
export interface VoucherPurchase {
  id: string;
  status: VoucherPurchaseStatus;
  buyer_email: string;
  buyer_first_name: string | null;
  buyer_last_name: string | null;
  buyer_name: string | null;
  ticket_count: number;
  unit_price_cents: number;
  amount_cents: number;
  currency: string;
  stripe_payment_intent_id: string | null;
  locale: string | null;
  email_sent_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface VoucherRedemptionDeparture {
  departure_number: string;
  departure_start: string;
  lifecycle_status: string;
  payment_status: string;
}

export interface VoucherRedemption {
  id: string;
  redeemed_at: string;
  amount_off_cents: number;
  booking_id: string;
  booking_number: string | null;
  customer_name: string | null;
  customer_email: string | null;
  departures: VoucherRedemptionDeparture[];
}

// One row of the merged timeline. `source` says whether it is a real
// booking.audit_log row or was derived from the purchase / code / redemption
// tables (pre-migration vouchers, first e-mail send, redemptions).
export interface VoucherEvent {
  id: string;
  created_at: string;
  actor_type: 'customer' | 'operator' | 'system' | 'stripe' | 'sweeper' | 'voucher-confirm';
  actor_label: string | null;
  action: string;
  from_value: unknown;
  to_value: unknown;
  metadata: unknown;
  source: 'audit' | 'derived';
}

export interface VoucherDetail {
  code: DiscountCodeRow & {
    max_party_size: number | null;
    created_by_email: string | null;
  };
  purchase: VoucherPurchase | null;
  redemptions: VoucherRedemption[];
  events: VoucherEvent[];
}

export async function fetchVoucherDetail(code: string): Promise<VoucherDetail | null> {
  const { data, error } = await supabase
    .schema('booking')
    .rpc('fn_admin_voucher_detail', { p_code: code });
  if (error) throw error;
  return (data as VoucherDetail | null) ?? null;
}

// Zoek page: code / notes / buyer e-mail / buyer name. Empty below 2 chars.
export async function searchVouchers(term: string, limit = 20): Promise<DiscountCodeRow[]> {
  if (term.trim().length < 2) return [];
  const { data, error } = await supabase
    .schema('booking')
    .rpc('fn_admin_search_vouchers', { p_search: term.trim(), p_limit: limit });
  if (error) throw error;
  return (data as DiscountCodeRow[]) ?? [];
}

export interface CreateDiscountCodeInput {
  code: string;
  kind: DiscountKind;
  percent_off: number | null;
  flat_off_cents: number | null;
  max_uses: number | null;
  valid_from: string | null;
  valid_until: string | null;
  notes: string | null;
  experience_id: string | null;     // null = universal
}

export interface BulkCreateDiscountCodesInput {
  count: number;
  prefix: string | null;
  kind: DiscountKind;
  percent_off: number | null;
  flat_off_cents: number | null;
  max_uses: number | null;
  valid_from: string | null;
  valid_until: string | null;
  notes: string | null;
  experience_id: string | null;
}

export interface BulkCreateResult {
  created_count: number;
  codes: Array<{ id: string; code: string }>;
}

export async function listDiscountCodes(
  includeInactive = false,
): Promise<DiscountCodeRow[]> {
  const { data, error } = await supabase
    .schema('booking')
    .rpc('fn_admin_list_discount_codes', {
      p_include_inactive: includeInactive,
    });
  if (error) throw error;
  return (data as DiscountCodeRow[]) ?? [];
}

export async function createDiscountCode(
  operatorId: string,
  input: CreateDiscountCodeInput,
): Promise<DiscountCodeRow> {
  const { data, error } = await supabase
    .schema('booking')
    .rpc('fn_admin_create_discount_code', {
      p_operator_id:    operatorId,
      p_code:           input.code,
      p_kind:           input.kind,
      p_percent_off:    input.percent_off,
      p_flat_off_cents: input.flat_off_cents,
      p_max_uses:       input.max_uses,
      p_valid_from:     input.valid_from,
      p_valid_until:    input.valid_until,
      p_notes:          input.notes,
      p_experience_id:  input.experience_id,
    });
  if (error) throw error;
  return data as DiscountCodeRow;
}

export async function createDiscountCodesBulk(
  operatorId: string,
  input: BulkCreateDiscountCodesInput,
): Promise<BulkCreateResult> {
  const { data, error } = await supabase
    .schema('booking')
    .rpc('fn_admin_create_discount_codes_bulk', {
      p_operator_id:    operatorId,
      p_count:          input.count,
      p_prefix:         input.prefix,
      p_kind:           input.kind,
      p_percent_off:    input.percent_off,
      p_flat_off_cents: input.flat_off_cents,
      p_max_uses:       input.max_uses,
      p_valid_from:     input.valid_from,
      p_valid_until:    input.valid_until,
      p_notes:          input.notes,
      p_experience_id:  input.experience_id,
    });
  if (error) throw error;
  return data as BulkCreateResult;
}

export interface UpdateDiscountCodeInput extends CreateDiscountCodeInput {
  is_active: boolean;
}

export async function updateDiscountCode(
  operatorId: string,
  codeId: string,
  input: UpdateDiscountCodeInput,
): Promise<void> {
  const { error } = await supabase
    .schema('booking')
    .rpc('fn_admin_update_discount_code', {
      p_operator_id:    operatorId,
      p_code_id:        codeId,
      p_code:           input.code,
      p_kind:           input.kind,
      p_percent_off:    input.percent_off,
      p_flat_off_cents: input.flat_off_cents,
      p_max_uses:       input.max_uses,
      p_valid_from:     input.valid_from,
      p_valid_until:    input.valid_until,
      p_notes:          input.notes,
      p_experience_id:  input.experience_id,
      p_is_active:      input.is_active,
    });
  if (error) throw error;
}

export async function markDiscountCodeUsed(
  operatorId: string,
  codeId: string,
  undo = false,
): Promise<void> {
  const { error } = await supabase
    .schema('booking')
    .rpc('fn_admin_mark_discount_code_used', {
      p_operator_id: operatorId,
      p_code_id:     codeId,
      p_undo:        undo,
    });
  if (error) throw error;
}

export async function deactivateDiscountCode(
  operatorId: string,
  codeId: string,
): Promise<void> {
  const { error } = await supabase
    .schema('booking')
    .rpc('fn_admin_deactivate_discount_code', {
      p_operator_id: operatorId,
      p_code_id:     codeId,
    });
  if (error) throw error;
}

export interface ValidateResult {
  valid: boolean;
  reason?: string;
  code_id?: string;
  code?: string;
  kind?: DiscountKind;
  percent_off?: number | null;
  flat_off_cents?: number | null;
  amount_off_cents?: number;
  new_total_cents?: number;
  // Cart-aware validation only.
  subtotal_cents?: number;
  allocation?: Array<{ pick_index: number; eligible: boolean; amount_off_cents: number }>;
}

// One experience-pick of a cart, in cart order (p_picks element of
// booking.fn_validate_discount_code_cart).
export interface DiscountPickInput {
  experience_id: string;
  party_size: number;
  subtotal_cents: number;
}

// Cart-aware preview: the same function booking-walkin-create runs at
// create time, so what the operator sees here is what gets charged.
export async function validateDiscountCodeCart(
  code: string,
  picks: DiscountPickInput[],
): Promise<ValidateResult> {
  const { data, error } = await supabase
    .schema('booking')
    .rpc('fn_validate_discount_code_cart', {
      p_code:  code,
      p_picks: picks,
    });
  if (error) throw error;
  return (data as ValidateResult) ?? { valid: false, reason: 'rpc_null' };
}

export async function validateDiscountCode(
  code: string,
  subtotalCents: number,
  experienceId: string | null = null,
  partySize: number | null = null,
): Promise<ValidateResult> {
  const { data, error } = await supabase
    .schema('booking')
    .rpc('fn_validate_discount_code', {
      p_code:           code,
      p_subtotal_cents: subtotalCents,
      p_experience_id:  experienceId,
      p_party_size:     partySize,
    });
  if (error) throw error;
  return (data as ValidateResult) ?? { valid: false, reason: 'rpc_null' };
}
